import React, { useState, useRef, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';

import Header from './Header';
import QuestionButton from './QuestionButton';
import SubmitButton from './SubmitButton';
import InputField from './InputField';

import getEnv from '../utils/getEnv';
import { HTTP_NOT_FOUND, HTTP_BAD_REQUEST } from '../constants';

/**
 * Functional component that initializes the address (login) form
 *
 * @param {object} props - properties given to the component
 */
const AddressForm = (props) => {
    const { t } = useTranslation();
    const [streetName, setStreetName] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [termsAccepted, toggleTermsAccepted] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [reCaptchaValue, setReCaptchaValue] = useState(null);
    const [errorFields, setErrorFields] = useState('');
    const [errorStyle, setErrorStyle] = useState({});
    const [buttonLoading, setButtonLoading] = useState('');
    const reCaptchaEl = useRef(null);

    let privateTermsLink, apiHost, reCaptchaSiteKey;
    if (getEnv('NODE_ENV') === 'production') {
        privateTermsLink = window._env_.REACT_APP_TERMS_LINK;
        apiHost = window._env_.REACT_APP_API_HOST;
        reCaptchaSiteKey = window._env_.REACT_APP_RECAPTCHA_SITE_KEY;
    } else {
        privateTermsLink = getEnv('REACT_APP_TERMS_LINK', 'https://www.keypro.fi/en/terms-of-service');
        apiHost = getEnv('REACT_APP_API_HOST', 'https://localhost:8085');
        reCaptchaSiteKey = '6Ld0BcAZAAAAAAg8IXUrV4UnDEg9yY2a2bSwKru1';
    }

    const apiUrl = `${apiHost}/address-authentication/`;

    const { step, setStep, setFormAddress, commonErrorMsg, setCommonErrorMsg, headers, setHeaders } = props;

    /**
     * Checks if form is given all the info it needs and reCaptcha is solved, then sets Submit button
     * disabled and uses axios to send a POST request to backend. If error, message is shown to user, if no errors,
     * set the next form step and set jwt to header.
     *
     * @param {*} e - the onSubmit event
     */
    const submitAddressForm = (e) => {
        if (!streetName || !zipCode) {
            setErrorFields(!streetName && !zipCode ? 'both' : !streetName ? 'address' : 'zipcode');
            setErrorStyle({ borderColor: '#ff2222' });
            reCaptchaEl.current.reset();
        } else if (!reCaptchaValue) {
            setErrorMsg(t('recaptchaNotSolved'));
        } else {
            setButtonLoading(t('searching'));
            const postData = new URLSearchParams();
            const splitRegex = /([a-zA-ZäÄöÖåÅ\-\s]+)([0-9-a-zA-ZäÄöÖåÅ]+)/;
            const addressMatch = streetName.match(splitRegex);
            if (!addressMatch) {
                setErrorMsg(t('pleaseInputAddress'));
                setErrorFields('address');
                setErrorStyle({ borderColor: '#ff2222' });
                setButtonLoading('');
                return;
            }
            postData.append('streetName', addressMatch[1].trim());
            postData.append('streetNo', addressMatch[2].trim());
            postData.append('zipcode', zipCode);

            const config = {
                headers: headers,
            };

            axios
                .post(apiUrl, postData, config)
                .then((resp) => {
                    setFormAddress(resp.data.address_id);
                    setHeaders({ ...headers, 'JWT-address': resp.data.jwt });
                    setStep((prevStep) => prevStep + 1);
                })
                .catch((err) => {
                    if (!err.response) {
                        console.log(`%cError: ${err}`, 'color: #E8178C');
                        setErrorMsg(t('errorUnexpected'));
                        setButtonLoading('');
                        return;
                    }
                    switch (err.response.status) {
                        case HTTP_NOT_FOUND:
                            setErrorMsg(t('addressNotFound'));
                            console.log(`%cNotFound: ${err.response.data.detail}`, 'color: #E8178C');
                            break;
                        case HTTP_BAD_REQUEST:
                            setErrorMsg(t('pleaseInputAddress'));
                            console.log(`%cBadRequest: ${err.response.data.detail}`, 'color: #E8178C');
                            break;
                        default:
                            console.log(`%cError: ${err.response.data.detail}`, 'color: #E8178C');
                            setErrorMsg(t('errorUnexpected'));
                    }
                    setButtonLoading('');
                    reCaptchaEl.current.reset();
                });

            e.preventDefault();
        }
    };

    /**
     * Handles the onChange events on input fields.
     *
     * @param {*} e - the event fired (onChange)
     * @param {Function} changeHandler - function to handle the changes (setState hook)
     */
    const handleChange = (e, changeHandler) => {
        changeHandler(e.target.value);
        setErrorFields('');
        setErrorStyle({});
        if (commonErrorMsg) {
            setCommonErrorMsg('');
        }
    };

    const localProps = {
        step: step,
        setStep: setStep,
        infoText: t('insertApartmentInfo'),
        headerText: t('startSmsHeader'),
        submitText: t('submitContinueContacts'),
        submitTooltip: t('submitContinueContacts'),
        styleBtn: { background: 'white', borderColor: '#ccc' },
        styleSpan: { border: '3px solid black', color: 'black' },
        stylePopup: { top: 'auto', left: '68%' },
        streetName: streetName,
        zipCode: zipCode,
        reCaptchaValue: reCaptchaValue,
        errorFields: errorFields,
        enableButton: termsAccepted,
        loadingButton: buttonLoading,
        setStreetName: setStreetName,
        setZipCode: setZipCode,
        setErrorMsg: setErrorMsg,
        setErrorFields: setErrorFields,
        submitForm: submitAddressForm,
    };

    return (
        <Fragment>
            <div className="form-signin">
                <Header {...localProps} />
                <h4 className="text-align-center">{t('addressFailure')}</h4>
                <h6 className="text-align-center">{t('noElContract')}</h6>
            </div>

            <div className="col-md-offset-2 col-md-8">
                <form id="authenticate-address" className="form-horizontal">
                    <div className="row">
                        <div className="col-sm-4 text-align-right">
                            <h3 className="registration-form-title">{t('addrInfo')}</h3>
                        </div>
                    </div>
                    <div className="form-group required">
                        <label htmlFor="address" className="col-sm-4 control-label">
                            {t('addr')}
                        </label>
                        <div className="col-sm-8">
                            <div className="input-group">
                                <InputField
                                    {...{
                                        inputName: 'address',
                                        placeHolder: t('streetPlaceholder'),
                                        inputTitle: t('insertApartmentInfo'),
                                        inputValue: streetName,
                                        changeHandler: handleChange,
                                        setHandler: setStreetName,
                                        inputStyle: errorStyle,
                                    }}
                                />
                                <span className="input-group-btn info-button-container">
                                    <QuestionButton {...localProps} />
                                </span>
                            </div>
                            {errorFields === 'address' || errorFields === 'both' ? (
                                <p className="help-block help-block-red">{t('fieldRequired')}</p>
                            ) : null}
                        </div>
                    </div>
                    <div className="form-group required">
                        <label htmlFor="zipcode" className="col-sm-4 control-label">
                            {t('zipCode')}
                        </label>
                        <div className="col-sm-8">
                            <InputField
                                {...{
                                    inputName: 'zipcode',
                                    placeHolder: t('zipCode'),
                                    inputTitle: t('insertZip'),
                                    inputValue: zipCode,
                                    changeHandler: handleChange,
                                    setHandler: setZipCode,
                                    inputStyle: errorStyle,
                                }}
                            />
                            {errorFields === 'zipcode' || errorFields === 'both' ? (
                                <p className="help-block help-block-red">{t('fieldRequired')}</p>
                            ) : null}
                        </div>
                    </div>

                    <div className="col-sm-offset-4 col-sm-7 padding-left-none">
                        <label>
                            <InputField
                                {...{
                                    inputName: 'terms_accepted',
                                    inputType: 'checkbox',
                                    changeHandler: toggleTermsAccepted,
                                    inputCheck: termsAccepted,
                                }}
                            />
                            &nbsp;{t('usingSystem')}
                            <a href={privateTermsLink} target="_blank" rel="noopener noreferrer">
                                &nbsp;{t('privTerms')}
                            </a>
                            ,
                            <a
                                href="https://www.keypro.fi/en/terms-of-service"
                                target="_blank"
                                rel="noopener noreferrer">
                                &nbsp;{t('keyproTerms')}
                            </a>
                            ,
                            <a href="https://www.keypro.fi/en/privacy-policy" target="_blank" rel="noopener noreferrer">
                                &nbsp;{t('keyproPrivacyPol')}
                            </a>
                            &nbsp;{t('useCookies')}
                        </label>
                    </div>

                    <div className="form-group ">
                        <div className="col-sm-offset-4 col-sm-8">
                            <ReCAPTCHA
                                ref={(r) => (reCaptchaEl.current = r)}
                                sitekey={reCaptchaSiteKey}
                                onChange={(val) => setReCaptchaValue(val)}
                            />
                        </div>
                    </div>
                    {errorMsg && <p className="help-block help-block-red text-align-center">{errorMsg}</p>}
                    {commonErrorMsg && <p className="help-block help-block-red text-align-center">{commonErrorMsg}</p>}
                </form>
            </div>

            <div className="container">
                <div className="col-sm-offset-2 col-sm-9" align="center">
                    <SubmitButton {...localProps} />
                </div>
            </div>
        </Fragment>
    );
};

export default AddressForm;
